import React, { Component } from 'react';
import * as R from 'ramda';
import { WithTranslation, withTranslation } from 'react-i18next';
import Icon from '../icon';
import { combineClassNames } from '../../utils/combineClassNames';
import { Button } from '../button';
// import { Sticky } from 'semantic-ui-react';

type SearchType = string | null;

export type Props = WithTranslation & {
  debounce: boolean;
  filter: boolean;
  onSearch: (search: SearchType) => void;
  filters?: string[];
  removeFilter?: (type: string) => void;
  defaultFocus?: boolean;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  className?: string;
  clearable?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
};

type State = {
  search: string | null,
};

class UnconnectedSearchBar extends Component<Props, State> {
  static props: Props;

  debounceTimer: ReturnType<typeof setTimeout> | null;

  static defaultProps = {
    debounce: true,
    filter: true,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      search: props.defaultValue || '',
    };

    this.debounceTimer = null as ReturnType<typeof setTimeout> | null;
  }

  debouncedNotifySearch = (value: SearchType) => {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    const { debounce, filter, onSearch } = this.props;
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (filter) {
        const isClear = value === null || value === '';
        const isLongEnough = typeof value === 'string' && value.length >= 3;
        if (!isClear && !isLongEnough) return;
      }
      onSearch(value);
    }, debounce ? 500 : 0);
  };

  componentWillUnmount() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.defaultValue !== this.props.defaultValue) {
      this.updateSearch(this.props.defaultValue || '');
    }
  }

  updateSearch = (search: string) => {
    this.setState({ search });
  };

  handleSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { search: oldSearch } = this.state;
    const { filter } = this.props;
    const search = e.target.value;

    // Update input value immediately
    this.setState({ search });

    // When filter is on, shrink from 3+ chars to <3 must clear the applied search
    const valueToNotify =
      oldSearch && search.length < 3 && oldSearch.length >= 3 && filter ? null : search;
    this.debouncedNotifySearch(valueToNotify);
  };

  clearSearch = () => {
    this.setState({ search: '' });
    this.debouncedNotifySearch('');
  };

  render() {
    const {
      t,
      className,
      defaultFocus,
      filters,
      removeFilter,
      placeholder,
      ...props
    } = this.props;

    return (
      <div role="search" className={combineClassNames('Form SearchBar', className)}>
        <Icon type="search" />
        {filters && filters.map((filter) => (
          <div className="SearchBar__Filter" key={filter}>
            {filter}
            <Icon type="close" onClick={removeFilter && (() => removeFilter(filter))} />
          </div>
        ))}
        <input
          ref={defaultFocus ? ((ref) => ref && ref.focus()) : undefined}
          className="Form__control"
          value={this.state.search || ''}
          onChange={this.handleSearch}
          placeholder={placeholder || t('common:search_bar_placeholder')}
          onKeyPress={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
          onKeyDown={(e) => {
            if (e.code === 'Space') e.stopPropagation();
          }}
          {...R.omit([
            'onSearch', 'filter', 'debounce', 'onChange', 'tReady', 'i18n',
            'clearable', 'value', 'defaultValue',
          ], props)}
        />

        {this.props.clearable && this.state.search && (
          <Button
            size="small"
            onClick={this.clearSearch}
            icon="close"
            className="tw-mr-2"
          />
        )}
      </div>
    );
  }
}

// TODO https://flex-appeal.atlassian.net/browse/PD-8098
// const UnconnectedStickySearchBar = (props: any) => {
//   return <Sticky>
//   </Sticky>;
// };
// export const StickySearchBar = withTranslation()(UnconnectedSearchBar);

export const SearchBar = withTranslation()(UnconnectedSearchBar);
